/**
 * TargetSelector Component
 *
 * Allows users to select between execution targets (Server, ESP32, All).
 * Updates the node registry store to filter available nodes.
 */

import React from 'react';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Server, Cpu, Globe } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useNodeRegistryStore, targetLabels } from '@/stores/workflow/node-registry-store';
import { ExecutionTarget } from '@/types/workflow-nodes';

interface TargetSelectorProps {
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  showLabels?: boolean;
}

const targetIcons: Record<ExecutionTarget, React.ReactNode> = {
  [ExecutionTarget.SERVER]: <Server className="h-4 w-4" />,
  [ExecutionTarget.ESP32]: <Cpu className="h-4 w-4" />,
  [ExecutionTarget.ALL]: <Globe className="h-4 w-4" />,
};

const targetDescriptions: Record<ExecutionTarget, string> = {
  [ExecutionTarget.SERVER]: 'Cloud-based execution with full API access',
  [ExecutionTarget.ESP32]: 'On-device execution with hardware access',
  [ExecutionTarget.ALL]: 'Show all available nodes',
};

export const TargetSelector: React.FC<TargetSelectorProps> = ({
  className,
  size = 'md',
  showLabels = true,
}) => {
  const { selectedTarget, setSelectedTarget, isLoading } = useNodeRegistryStore();

  const handleTargetChange = (value: string) => {
    setSelectedTarget(value as ExecutionTarget);
  };

  const sizeClasses = {
    sm: 'h-8 text-xs',
    md: 'h-10 text-sm',
    lg: 'h-12 text-base',
  };

  return (
    <div className={cn('flex flex-col gap-1', className)}>
      <Tabs
        value={selectedTarget}
        onValueChange={handleTargetChange}
        className="w-full"
      >
        <TabsList
          className={cn(
            'grid w-full grid-cols-3 bg-muted/50',
            sizeClasses[size],
            isLoading && 'opacity-50 pointer-events-none'
          )}
        >
          {Object.values(ExecutionTarget).map((target) => (
            <TabsTrigger
              key={target}
              value={target}
              className={cn(
                'flex items-center gap-2 transition-all',
                'data-[state=active]:bg-background data-[state=active]:shadow-sm',
                size === 'sm' && 'px-2 py-1',
                size === 'md' && 'px-3 py-1.5',
                size === 'lg' && 'px-4 py-2'
              )}
              title={targetDescriptions[target]}
              aria-label={`Select ${targetLabels[target]} target`}
            >
              {targetIcons[target]}
              {showLabels && (
                <span className={size === 'sm' ? 'hidden sm:inline' : ''}>
                  {targetLabels[target]}
                </span>
              )}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
      {size !== 'sm' && (
        <p className="text-xs text-muted-foreground text-center">
          {targetDescriptions[selectedTarget]}
        </p>
      )}
    </div>
  );
};

export default TargetSelector;
